/**
 * Legacy MapContainer component using @react-google-maps/api.
 * This component creates a new map instance on each render.
 *
 * NOTE: SearchPage now uses MapContext + MapContent
 * for persistent map instances. This component is still used by:
 * - CartPage
 * - ResourceListDetailPage
 *
 * For new pages that need persistent maps, use MapContext instead.
 */
import React from "react";
import { GoogleMap } from "@react-google-maps/api";
import MapMarker from "./MapMarker";
import MapInfoWindow from "./MapInfoWindow";
import { useOrg } from "../../contexts/OrganizationContext";
import { coordInLocale } from "../../utils/geo";

const MAP_STYLES = [
  {
    stylers: [{ visibility: "simplified" }, { saturation: 20 }, { weight: 3.2 }, { lightness: 25 }],
  },
  {
    featureType: "water",
    stylers: [{ weight: 0.1 }],
  },
  {
    featureType: "administrative.province",
    stylers: [{ visibility: "on" }, { weight: 1.5 }],
  },
  {
    featureType: "administrative.country",
    stylers: [{ visibility: "on" }, { weight: 1.5 }],
  },
];

const DEFAULT_ZOOM = 8;

const containerStyle = {
  height: "100%",
};

function MapContainer({
  center,
  results = [],
  onDragEnd,
  onMarkerClick,
  selectedResultId,
  highlightedResultId,
  mapZoom,
}) {
  const { organization } = useOrg();

  // The Maps API is loaded via the layout's script tag (not dynamically).
  // In test mode without TEST_MAPS, a mock google.maps exists but doesn't support
  // the full API that @react-google-maps/api requires. Detect the real API by checking
  // for a method the mock doesn't have.
  const isRealMapsApi =
    typeof google !== "undefined" &&
    typeof google.maps !== "undefined" &&
    typeof google.maps.event?.removeListener === "function";

  const mapRef = React.useRef(null);

  const onLoad = React.useCallback((map) => {
    mapRef.current = map;
  }, []);

  const onUnmount = React.useCallback(() => {
    mapRef.current = null;
  }, []);

  const handleDragEnd = React.useCallback(() => {
    if (mapRef.current && onDragEnd) {
      const newCenter = mapRef.current.getCenter();
      onDragEnd({
        lat: newCenter.lat(),
        lng: newCenter.lng(),
      });
    }
  }, [onDragEnd]);

  // Fit bounds when results change
  React.useEffect(() => {
    if (!mapRef.current || !results.length) return;
    if (mapZoom) {
      mapRef.current.setZoom(mapZoom);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    let validMarkers = 0;
    const hasLocale = organization.serviceLocale?.geo?.bounds;
    results.forEach((result) => {
      if (result.latLng && result.latLng.lat && result.latLng.lng) {
        if (!hasLocale || coordInLocale(result.latLng, organization)) {
          bounds.extend(result.latLng);
          validMarkers++;
        }
      }
    });

    if (validMarkers > 0) {
      mapRef.current.fitBounds(bounds);
      if (validMarkers === 1) {
        google.maps.event.addListenerOnce(mapRef.current, "idle", () => {
          mapRef.current.setZoom(15);
        });
      }
    }
  }, [results, mapZoom]);

  const viewportWidth = typeof window !== "undefined" ? Math.max(document.documentElement.clientWidth, 0) : 1024;

  const options = React.useMemo(
    () => ({
      zoom: DEFAULT_ZOOM,
      gestureHandling: "cooperative",
      styles: MAP_STYLES,
      mapTypeControl: viewportWidth >= 450,
      mapTypeControlOptions:
        viewportWidth >= 450 ? { style: google.maps.MapTypeControlStyle.DROPDOWN_MENU } : undefined,
    }),
    [viewportWidth]
  );

  if (!isRealMapsApi) return <div className="map-container" style={containerStyle} />;

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      mapContainerClassName="map-container"
      center={center}
      options={options}
      onLoad={onLoad}
      onUnmount={onUnmount}
      onDragEnd={handleDragEnd}
    >
      {results.map((result) => {
        if (!result.latLng || !result.latLng.lat || !result.latLng.lng) return null;
        return (
          <MapMarker
            key={result.program_site_id}
            result={result}
            isSelected={selectedResultId === result.program_site_id}
            isHighlighted={highlightedResultId === result.program_site_id}
            onClick={onMarkerClick}
          />
        );
      })}
      {selectedResultId &&
        (() => {
          const selected = results.find((r) => r.program_site_id === selectedResultId);
          return selected ? (
            <MapInfoWindow result={selected} onClose={() => onMarkerClick && onMarkerClick(null)} />
          ) : null;
        })()}
    </GoogleMap>
  );
}

export default MapContainer;
